// Push Notification Utility
// Smart backend URL detection - prioritizes runtime domain detection for production
const getBackendUrl = () => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    
    // For custom production domains (like app.outsiderstravel.gr), use same origin
    if (!hostname.includes('preview.emergentagent.com') && 
        !hostname.includes('emergent.host') && 
        !hostname.includes('localhost') &&
        !hostname.includes('127.0.0.1')) {
      return window.location.origin;
    }
  }
  
  // For Emergent preview environments or local development, use the env variable
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  
  return '';
};

const API = `${getBackendUrl()}/api`;

// Convert URL-safe base64 to Uint8Array for applicationServerKey
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Check if push notifications are supported
export function isPushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

// Get current notification permission status
export function getNotificationPermission() {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
}

// Request notification permission
export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    console.log('Notifications not supported');
    return 'unsupported';
  }
  
  const permission = await Notification.requestPermission();
  console.log('Notification permission:', permission);
  return permission;
}

// Register the push service worker
export async function registerPushServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    console.log('Service workers not supported');
    return null;
  }
  
  try {
    const registration = await navigator.serviceWorker.register('/push-sw.js', {
      scope: '/'
    });
    console.log('Push Service Worker registered:', registration);
    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    return null;
  }
}

// Subscribe to push notifications
export async function subscribeToPush(token) {
  if (!isPushSupported()) {
    console.log('Push notifications not supported');
    return null;
  }
  
  try {
    // Get VAPID public key from backend
    const vapidResponse = await fetch(`${API}/push/vapid-public-key`);
    const { publicKey } = await vapidResponse.json();
    
    if (!publicKey) {
      console.error('No VAPID public key available');
      return null;
    }
    
    // Register service worker if not already registered
    let registration = await navigator.serviceWorker.getRegistration('/');
    if (!registration) {
      registration = await registerPushServiceWorker();
    }
    
    if (!registration) {
      console.error('Could not register service worker');
      return null;
    }
    
    // Wait for service worker to be ready
    await navigator.serviceWorker.ready;
    
    // Subscribe to push
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey)
    });
    
    console.log('Push subscription:', subscription);
    
    // Send subscription to backend
    const subscriptionJson = subscription.toJSON();
    const response = await fetch(`${API}/push/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        endpoint: subscriptionJson.endpoint,
        keys: subscriptionJson.keys
      })
    });
    
    if (response.ok) {
      const result = await response.json();
      console.log('Subscription saved to backend:', result);
      return subscription;
    } else {
      console.error('Failed to save subscription to backend');
      return null;
    }
  } catch (error) {
    console.error('Error subscribing to push:', error);
    return null;
  }
}

// Unsubscribe from push notifications
export async function unsubscribeFromPush(token) {
  try {
    const registration = await navigator.serviceWorker.getRegistration('/');
    if (registration) {
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
      }
    }
    
    // Also remove from backend
    await fetch(`${API}/push/unsubscribe`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    console.log('Unsubscribed from push notifications');
    return true;
  } catch (error) {
    console.error('Error unsubscribing from push:', error);
    return false;
  }
}

// Check if user is already subscribed
export async function isPushSubscribed() {
  if (!isPushSupported()) {
    return false;
  }
  
  try {
    const registration = await navigator.serviceWorker.getRegistration('/');
    if (!registration) {
      return false;
    }
    
    const subscription = await registration.pushManager.getSubscription();
    return subscription !== null;
  } catch (error) {
    console.error('Error checking push subscription:', error);
    return false;
  }
}

// Show a local notification (for testing or immediate feedback)
export function showLocalNotification(title, body, options = {}) {
  if (Notification.permission === 'granted') {
    new Notification(title, {
      body,
      icon: '/logo192.png',
      badge: '/logo192.png',
      ...options
    });
  }
}
